import {
  Box,
  CheckboxCard as ChakraCheckboxCard,
  RadioCard as ChakraRadioCard,
  CheckboxGroup,
  Fieldset,
  Flex,
  Grid,
} from '@chakra-ui/react';
import { useId } from 'react';
import { FieldValues, UseControllerProps } from 'react-hook-form';

import { useField } from '#features/form/hooks/useField';
import { formSelectionIconSize } from '#features/ui/theme/form-selection';
import { CheckboxCard } from '../../CheckboxCard';
import { HelpPopover } from '../../HelpPopover';
import { Image } from '../../Image';
import { RadioCardItem, RadioCardRoot } from '../../RadioCard';
import { ErrorMessage } from '../ErrorMessage';
import { InputProps } from '../types';

type OptionItem = {
  value: string | number;
  label: React.ReactNode;
  image?: string;
  disabled?: boolean;
  /** A checked default that does not restrict results, such as “All”. */
  selectionTone?: 'accent' | 'neutral';
};

type OptionsGroupProps = {
  options: OptionItem[];
  accessibleLabel?: string;
  pillJustify?: 'start' | 'center';
  onReselect?: (value: string | number) => void;
  /** Accessible name for important help, with keyboard and touch access. */
  helperLabel?: string;
  /** Short visible explanation, associated with the choices for screen readers. */
  description?: string;
  /** Optional action belonging to this field, such as clearing a single choice. */
  action?: React.ReactNode;
  /** Focus target for form validation on pill fields. */
  inputRef?: React.Ref<HTMLInputElement>;
  variant?: 'card' | 'pill';
  /** Optional single pills can be cleared by clicking their selected choice again. */
  clearable?: boolean;
  /** Equal-width pills with 44px touch targets; scroll when the row cannot fit. */
  pillColumns?: { compact: number; wide: number };
} & (
  | ({
      selectType: 'multiple';
    } & InputProps<(string | number)[]>)
  | ({
      selectType: 'single';
    } & InputProps<string | number>)
);

type InternalSingleProps = {
  pillJustify?: 'start' | 'center';
  onReselect?: (value: string | number) => void;
  clearable?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  disabled?: boolean;
  options: OptionItem[];
  name: string;
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (value: unknown) => void;
  isRequired?: boolean;
  label?: string;
  pillColumns?: { compact: number; wide: number };
  descriptionId?: string;
};

type InternalMultiProps = {
  inputRef?: React.Ref<HTMLInputElement>;
  disabled?: boolean;
  options: OptionItem[];
  name: string;
  value?: (string | number)[];
  defaultValue?: (string | number)[];
  onChange?: (value: unknown) => void;
};

// --- Card variants (existing) ---

const CardSingleSelect = ({
  disabled,
  name,
  onChange,
  value,
  options,
  defaultValue,
  label,
  descriptionId,
}: InternalSingleProps) => {
  return (
    <RadioCardRoot
      disabled={disabled}
      aria-label={label}
      aria-describedby={descriptionId}
      orientation="horizontal"
      align="center"
      justify="center"
      maxW="lg"
      value={value != null ? String(value) : undefined}
      defaultValue={defaultValue != null ? String(defaultValue) : undefined}
      name={name}
      onValueChange={({ value: newValue }) => {
        onChange?.(newValue);
      }}
    >
      <Fieldset.Content asChild>
        <Flex gap={2} flexWrap="wrap" flexDirection="row">
          {options.map((option) => (
            <RadioCardItem
              key={option.value}
              disabled={option.disabled}
              value={String(option.value)}
              label={option.label}
              icon={
                option.image ? (
                  <Box mr={2} borderRadius="l1" overflow="hidden">
                    <Image src={option.image} width={30} height={30} alt={String(option.value)} />
                  </Box>
                ) : undefined
              }
              indicator={false}
            />
          ))}
        </Flex>
      </Fieldset.Content>
    </RadioCardRoot>
  );
};

const CardMultiSelect = ({
  name,
  onChange,
  value,
  options,
  defaultValue,
  disabled,
}: InternalMultiProps) => {
  return (
    <CheckboxGroup
      value={value?.map(String)}
      defaultValue={defaultValue?.map(String)}
      onValueChange={(newValue) => onChange?.(newValue)}
      name={name}
    >
      <Fieldset.Content asChild>
        <Flex gap={2} flexWrap="wrap" flexDirection="row">
          {options.map((option) => (
            <CheckboxCard
              disabled={disabled || option.disabled}
              key={option.value}
              value={String(option.value)}
              label={option.label}
              icon={
                option.image ? (
                  <Box mr={2} borderRadius="l1" overflow="hidden">
                    <Image src={option.image} width={30} height={30} alt={String(option.value)} />
                  </Box>
                ) : undefined
              }
            />
          ))}
        </Flex>
      </Fieldset.Content>
    </CheckboxGroup>
  );
};

// --- Pill variants ---

const PillSingleSelect = ({
  pillJustify,
  onReselect,
  clearable,
  inputRef,
  disabled,
  name,
  onChange,
  value,
  options,
  defaultValue,
  label,
  isRequired,
  pillColumns,
  descriptionId,
}: InternalSingleProps) => {
  return (
    <ChakraRadioCard.Root
      disabled={disabled}
      variant={'pill' as 'outline'}
      aria-label={label}
      aria-describedby={descriptionId}
      aria-required={isRequired}
      value={value != null ? String(value) : undefined}
      defaultValue={defaultValue != null ? String(defaultValue) : undefined}
      name={name}
      onValueChange={({ value: newValue }) => onChange?.(newValue)}
    >
      <Box
        containerType={pillColumns ? 'inline-size' : undefined}
        overflowX={pillColumns ? 'auto' : undefined}
      >
        <Grid
          rowGap={2}
          columnGap={
            pillColumns
              ? `clamp(0px, calc((100cqw - ${pillColumns.compact} * var(--chakra-sizes-11)) / ${Math.max(pillColumns.compact - 1, 1)}), var(--chakra-spacing-2))`
              : 2
          }
          display={pillColumns ? 'grid' : 'flex'}
          flexWrap="wrap"
          justifyContent={pillJustify}
          templateColumns={
            pillColumns
              ? `repeat(${pillColumns.compact}, minmax(var(--chakra-sizes-11), 1fr))`
              : undefined
          }
          css={
            pillColumns
              ? {
                  '@container (min-width: 400px)': {
                    gridTemplateColumns: `repeat(${pillColumns.wide}, minmax(var(--chakra-sizes-11), 1fr))`,
                    columnGap: `clamp(0px, calc((100cqw - ${pillColumns.wide} * var(--chakra-sizes-11)) / ${Math.max(pillColumns.wide - 1, 1)}), var(--chakra-spacing-2))`,
                  },
                }
              : undefined
          }
        >
          {options.map((option, index) => (
            <ChakraRadioCard.Item
              onClickCapture={(event) => {
                if (
                  !clearable &&
                  !disabled &&
                  !option.disabled &&
                  event.detail > 0 &&
                  !(event.target instanceof HTMLInputElement) &&
                  event.currentTarget.getAttribute('data-state') === 'checked'
                ) {
                  onReselect?.(option.value);
                }
                if (
                  clearable &&
                  !isRequired &&
                  !disabled &&
                  !option.disabled &&
                  event.detail > 0 &&
                  String(value ?? defaultValue) === String(option.value)
                ) {
                  event.preventDefault();
                  event.stopPropagation();
                  onChange?.('');
                }
              }}
              onKeyDownCapture={(event) => {
                if (
                  event.key === ' ' &&
                  !event.repeat &&
                  !clearable &&
                  !disabled &&
                  !option.disabled &&
                  event.currentTarget.getAttribute('data-state') === 'checked'
                ) {
                  onReselect?.(option.value);
                }
                if (
                  event.key === ' ' &&
                  clearable &&
                  !isRequired &&
                  !disabled &&
                  !option.disabled
                ) {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!event.repeat) {
                    onChange?.(
                      String(value ?? defaultValue) === String(option.value) ? '' : option.value
                    );
                  }
                }
              }}
              disabled={option.disabled}
              key={option.value}
              value={String(option.value)}
              h={pillColumns ? 11 : undefined}
              borderRadius={pillColumns ? 'l2' : undefined}
              _checked={
                option.selectionTone === 'neutral'
                  ? {
                      bg: 'bg.muted',
                      borderColor: 'border',
                      color: 'fg',
                      _hover: { bg: 'bg.emphasized' },
                    }
                  : pillColumns
                    ? { color: 'fg.emphasized' }
                    : undefined
              }
            >
              <ChakraRadioCard.ItemHiddenInput ref={index === 0 ? inputRef : undefined} />
              <ChakraRadioCard.ItemControl
                py={pillColumns ? 0 : undefined}
                alignItems={pillColumns ? 'center' : undefined}
                justifyContent={pillColumns ? 'center' : undefined}
              >
                {option.image && (
                  <Box
                    borderRadius="l1"
                    overflow="hidden"
                    boxSize={formSelectionIconSize}
                    flexShrink={0}
                  >
                    <Image src={option.image} width={24} height={24} alt="" />
                  </Box>
                )}
                <ChakraRadioCard.ItemText textAlign={pillColumns ? 'center' : undefined}>
                  {option.label}
                </ChakraRadioCard.ItemText>
              </ChakraRadioCard.ItemControl>
            </ChakraRadioCard.Item>
          ))}
        </Grid>
      </Box>
    </ChakraRadioCard.Root>
  );
};

const PillMultiSelect = ({
  inputRef,
  name,
  onChange,
  value,
  options,
  defaultValue,
  disabled,
}: InternalMultiProps) => {
  return (
    <CheckboxGroup
      value={value?.map(String)}
      defaultValue={defaultValue?.map(String)}
      onValueChange={(newValue) => onChange?.(newValue)}
      name={name}
    >
      <Flex gap={2} flexWrap="wrap">
        {options.map((option, index) => (
          <ChakraCheckboxCard.Root
            disabled={disabled || option.disabled}
            key={option.value}
            value={String(option.value)}
            variant={'pill' as 'outline'}
          >
            <ChakraCheckboxCard.HiddenInput ref={index === 0 ? inputRef : undefined} />
            <ChakraCheckboxCard.Control>
              {option.image && (
                <Box
                  borderRadius="l1"
                  overflow="hidden"
                  boxSize={formSelectionIconSize}
                  flexShrink={0}
                >
                  <Image src={option.image} width={24} height={24} alt="" />
                </Box>
              )}
              <ChakraCheckboxCard.Label>{option.label}</ChakraCheckboxCard.Label>
            </ChakraCheckboxCard.Control>
          </ChakraCheckboxCard.Root>
        ))}
      </Flex>
    </CheckboxGroup>
  );
};

// --- Main component ---

const RequiredIndicator = () => (
  <Box color="fg.error" lineHeight="none">
    *
  </Box>
);

export const OptionsGroup = ({
  label,
  accessibleLabel,
  pillJustify,
  onReselect,
  helperText,
  helperLabel,
  description,
  action,
  disabled,
  inputRef,
  error,
  selectType,
  isRequired,
  variant = 'card',
  clearable,
  pillColumns,
  ...props
}: OptionsGroupProps) => {
  const descriptionId = useId();
  const fieldsetId = useId();
  const legendId = `fieldset::${fieldsetId}::legend`;
  const withHelperText = Boolean(helperText);
  const hasLabel = Boolean(label);
  const hasError = Boolean(error);
  const isPill = variant === 'pill';

  const renderSelect = () => {
    const { options, name, value, defaultValue, onChange } = props;

    if (selectType === 'single') {
      const singleProps: InternalSingleProps = {
        pillJustify,
        onReselect,
        clearable,
        inputRef,
        disabled,
        options,
        name,
        value: value as string | number | undefined,
        defaultValue: defaultValue as string | number | undefined,
        onChange,
        isRequired,
        label: accessibleLabel ?? (typeof label === 'string' ? label : undefined),
        pillColumns,
        descriptionId: description ? descriptionId : undefined,
      };
      return isPill ? <PillSingleSelect {...singleProps} /> : <CardSingleSelect {...singleProps} />;
    }

    const multiProps: InternalMultiProps = {
      inputRef,
      disabled,
      options,
      name,
      value: value as (string | number)[] | undefined,
      defaultValue: defaultValue as (string | number)[] | undefined,
      onChange,
    };
    return isPill ? <PillMultiSelect {...multiProps} /> : <CardMultiSelect {...multiProps} />;
  };

  return (
    <Fieldset.Root
      id={fieldsetId}
      invalid={hasError}
      spaceY={0}
      gap={isPill ? 1.5 : 4}
      aria-describedby={
        [
          description ? descriptionId : null,
          hasError ? `fieldset::${fieldsetId}::error-text` : null,
        ]
          .filter(Boolean)
          .join(' ') || undefined
      }
    >
      {(hasLabel || action) && (
        <Fieldset.Legend
          asChild
          display="flex"
          alignItems="flex-end"
          textStyle="title"
          color="fg.emphasized"
          gap={1}
        >
          <Box id={legendId}>
            <Box minW={0}>{label}</Box> {isRequired && <RequiredIndicator />}
            {withHelperText ? (
              <HelpPopover
                compact
                accessibleLabel={helperLabel || (typeof label === 'string' ? label : undefined)}
                renderAsHtml={!helperLabel}
              >
                {helperText}
              </HelpPopover>
            ) : null}
            {action ? <Box ms="auto">{action}</Box> : null}
          </Box>
        </Fieldset.Legend>
      )}
      {renderSelect()}
      {description ? (
        <Fieldset.HelperText id={descriptionId} textStyle="caption" color="fg.muted">
          {description}
        </Fieldset.HelperText>
      ) : null}
      {hasError && <ErrorMessage error={error} isFieldset />}
    </Fieldset.Root>
  );
};

export type OptionsGroupFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<OptionsGroupProps, 'value' | 'defaultValue' | 'ref'> & {
    /** Keep numeric form values when the radio DOM reports a string. */
    valueAsNumber?: boolean;
  };

export const OptionsGroupField = <T extends FieldValues>({
  name,
  control,
  defaultValue,
  onChange,
  valueAsNumber,
  ...props
}: OptionsGroupFieldProps<T>) => {
  const fieldProps = useField({
    control,
    defaultValue,
    name,
    onChange,
  });

  return (
    <OptionsGroup
      {...fieldProps}
      {...props}
      onChange={(value) => fieldProps.onChange(valueAsNumber ? Number(value) : value)}
    />
  );
};
